/**
 * adminContentQueries — the read-side admin tRPC queries for the `/admin/cms`
 * surface, plus the wire-shape types and list/get query keys shared across the
 * mutation modules. Part of the `useAdminContent` decomposition.
 *
 *   - `useQuery` keys are flat (`['admin', 'content', '<method>', ...args]`)
 *     for predictable cache invalidation; the exported key constants/builders
 *     are the single source of truth the mutation modules invalidate against.
 *   - The wire-shape types (`AdminContentListItem`, `AdminContentDetail`,
 *     `AdminContentShape`) are inferred from the tRPC procedure outputs and
 *     re-used by the mutation modules (e.g. the reconcile state carries an
 *     `AdminContentDetail`).
 */

import { type UseQueryResult, useQuery } from '@tanstack/react-query';

import type { WebTrpcClient } from '../../../trpc/client.js';
import { useAdminTrpc } from './adminTrpcContext.js';

// ---------------------------------------------------------------------
// Wire-shape types — inferred from the tRPC procedure outputs
// ---------------------------------------------------------------------

/**
 * Output of `admin.content.list`. A flat array of `ContentItemSummary`
 * DTOs. We name the type via a helper that captures the procedure's
 * inferred output so the union of shape literals (`'article'`,
 * `'home_card'`, …) flows through to the catalog component without
 * duplicating the alphabet here.
 */
export type AdminContentListItem = Awaited<
  ReturnType<WebTrpcClient['admin']['content']['list']['query']>
>[number];

/**
 * Output of `admin.content.get`. The full `ContentItemDetail` DTO.
 */
export type AdminContentDetail = Awaited<
  ReturnType<WebTrpcClient['admin']['content']['get']['query']>
>;

/** The six fixed catalogue descriptors resolved for one content locale. */
export type AdminFixedServiceEntry = Awaited<
  ReturnType<WebTrpcClient['admin']['content']['listFixedEntries']['query']>
>[number];

/** Shape literal enumerated by the API's `shapeSchema`. */
export type AdminContentShape = AdminContentListItem['shape'];

// ---------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------

export interface UseAdminContentListOptions {
  readonly includeRetired?: boolean;
  /**
   * Optional server-side shape filter. The template-source dialog uses this
   * existing list endpoint for eligible articles rather than downloading every
   * CMS shape and filtering client-side.
   */
  readonly shape?: AdminContentShape;
  /**
   * Whether React Query should fetch this existing list variant. This controls
   * observer activity only: it is intentionally excluded from the query key,
   * because enabled and disabled callers describe the same server result.
   */
  readonly enabled?: boolean;
  /**
   * Restrict the list to one locale. `admin.content.list` has always accepted
   * this and applies `AND ($2::text IS NULL OR card.locale = $2::text)`, so
   * omitting it returns EVERY locale — which is right for a lookup that filters
   * on its own afterwards, and wrong for any surface that derives an ordering
   * group. The server's group is `(shape, locale)` and `scopeSequence` restarts
   * per locale, so a caller that mixes locales sees two independent sequences
   * interleaved. Such a caller passes the locale it is displaying.
   */
  readonly locale?: AdminContentListItem['locale'];
}

/**
 * Stable prefix for every `admin.content.list` query-key variant.
 * Exported so invalidation sites can target the whole family without
 * restating the literal triple (the previous hand-coded `['admin',
 * 'content', 'list']` worked only by accident — it prefix-matched all
 * variants, but adding a sibling key would silently broaden the sweep
 * the next time a consumer typed the literal). Variants append their
 * own discriminator after this prefix (today: `{ includeRetired }`).
 */
export const ADMIN_CONTENT_LIST_QUERY_KEY = ['admin', 'content', 'list'] as const;
export const ADMIN_FIXED_ENTRIES_QUERY_KEY = ['admin', 'content', 'listFixedEntries'] as const;

/**
 * Read the structural fixed-service catalogue for one content locale. A
 * missing localized page is a successful nullable result; a failed query is
 * deliberately left distinct from an empty catalogue.
 */
export function useAdminFixedServiceEntries(
  locale: AdminContentListItem['locale'],
): UseQueryResult<readonly AdminFixedServiceEntry[], Error> {
  const client = useAdminTrpc();
  return useQuery<readonly AdminFixedServiceEntry[], Error>({
    queryKey: [...ADMIN_FIXED_ENTRIES_QUERY_KEY, { locale }],
    queryFn: () => client.admin.content.listFixedEntries.query({ locale }),
  });
}

/**
 * Query `admin.content.list`. The query key includes `includeRetired`
 * so toggling the filter does not hit cached results from the other
 * filter setting — and, for the same reason, `locale` when one is given.
 * Without that, a locale-filtered caller and an unfiltered one would share
 * a cache entry and whichever fetched first would answer for both, so
 * narrowing one surface would silently narrow every other.
 *
 * The discriminator omits `locale` entirely when it is absent rather than
 * carrying an explicit `undefined`, so the existing unfiltered callers keep
 * the exact key they have today and gain no refetch from this change.
 */
export function useAdminContentList(
  options: UseAdminContentListOptions = {},
): UseQueryResult<readonly AdminContentListItem[], Error> {
  const client = useAdminTrpc();
  const includeRetired = options.includeRetired ?? false;
  const enabled = options.enabled ?? true;
  const { locale, shape } = options;
  const discriminator = {
    includeRetired,
    ...(locale !== undefined ? { locale } : {}),
    ...(shape !== undefined ? { shape } : {}),
  };
  return useQuery<readonly AdminContentListItem[], Error>({
    queryKey: [...ADMIN_CONTENT_LIST_QUERY_KEY, discriminator],
    queryFn: () => client.admin.content.list.query(discriminator),
    enabled,
  });
}

/**
 * Build the `admin.content.get` query key for a single item id. Exported
 * so invalidation sites (e.g. the publish-success refetch that lets the
 * editor form re-derive `isPublishedAndClean` after a publish — D-094)
 * target the exact cache entry without restating the literal quad. The
 * key matches `useAdminContentGet`'s `queryKey` 1:1 — in fact the hook
 * builds its `queryKey` from this helper (below), so the query key and
 * every invalidation key share a single source of truth. Accepts `null`
 * for the hook's disabled (`id === null`) state.
 */
export const adminContentGetQueryKey = (id: string | null): readonly unknown[] => [
  'admin',
  'content',
  'get',
  id,
];

/**
 * Query `admin.content.get` for a single item by id. Disabled when
 * `id === null` (the catalog's "no item open" state).
 */
export function useAdminContentGet(id: string | null): UseQueryResult<AdminContentDetail, Error> {
  const client = useAdminTrpc();
  return useQuery<AdminContentDetail, Error>({
    queryKey: adminContentGetQueryKey(id),
    queryFn: () => {
      if (id === null) {
        // `enabled: false` below prevents this branch being hit, but
        // the queryFn signature still needs to return a promise; the
        // throw is unreachable in practice and surfaces the
        // type-narrow violation loudly if `enabled` ever lies.
        throw new Error('useAdminContentGet queryFn invoked while id was null');
      }
      return client.admin.content.get.query({ id });
    },
    enabled: id !== null,
  });
}
